import { useState } from 'react';
import { Button, Form, Modal, ProgressBar } from 'react-bootstrap';

const KEEP = '__keep__';

const booleanOptions = [
    { value: KEEP, label: 'Keep as is' },
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
];

function BulkEditCareRecipientsModal({ show, onHide, selectedCount, customerCareTypes, onApply, isApplying, progress }) {
    const [fieldSearch, setFieldSearch] = useState('');
    const [status, setStatus] = useState(KEEP);
    const [careTypeId, setCareTypeId] = useState(KEEP);
    const [discountEnabled, setDiscountEnabled] = useState(false);
    const [discountValue, setDiscountValue] = useState('');
    const [modShared, setModShared] = useState(KEEP);
    const [modImmobile, setModImmobile] = useState(KEEP);
    const [modEstate, setModEstate] = useState(KEEP);

    const resetFields = () => {
        setFieldSearch('');
        setStatus(KEEP);
        setCareTypeId(KEEP);
        setDiscountEnabled(false);
        setDiscountValue('');
        setModShared(KEEP);
        setModImmobile(KEEP);
        setModEstate(KEEP);
    };

    const handleClose = () => {
        if (isApplying) return;
        resetFields();
        onHide();
    };

    const handleApply = () => {
        const changes = {};
        if (status !== KEEP) changes.Status = status;
        if (careTypeId !== KEEP) changes.CustomerCareTypeID = careTypeId;
        if (discountEnabled) changes.Discount = discountValue;
        if (modShared !== KEEP) changes.mod_shared = modShared === 'true';
        if (modImmobile !== KEEP) changes.mod_immobile = modImmobile === 'true';
        if (modEstate !== KEEP) changes.mod_estate = modEstate === 'true';
        onApply(changes);
    };

    const fieldDefs = [
        { key: 'status', label: 'Status' },
        { key: 'careTypeId', label: 'Care Type' },
        { key: 'discount', label: 'Default Discount Percentage' },
        { key: 'modShared', label: 'Shared (Mod)' },
        { key: 'modImmobile', label: 'Immobile (Mod)' },
        { key: 'modEstate', label: 'Estate (Mod)' },
    ];

    const visibleKeys = new Set(
        fieldSearch.trim()
            ? fieldDefs.filter(f => f.label.toLowerCase().includes(fieldSearch.trim().toLowerCase())).map(f => f.key)
            : fieldDefs.map(f => f.key)
    );

    const progressPercent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '1.1rem' }}>
                    Bulk edit &nbsp;<span style={{ color: 'var(--theme-text-secondary)', fontWeight: 400 }}>|</span>&nbsp; {selectedCount} service recipient{selectedCount !== 1 ? 's' : ''}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted" style={{ fontSize: '0.9rem' }}>Modify any of the below fields to continue.</p>

                <Form.Control
                    type="text"
                    placeholder="Search for fields..."
                    value={fieldSearch}
                    onChange={e => setFieldSearch(e.target.value)}
                    className="mb-3"
                    size="sm"
                />

                <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden' }}>
                    {visibleKeys.has('status') && (
                        <div style={rowStyle}>
                            <span style={labelStyle}>Status</span>
                            <Form.Control as="select" size="sm" value={status} onChange={e => setStatus(e.target.value)} style={controlStyle}>
                                <option value={KEEP}>Keep as is</option>
                                <option value="ACTIVE">Active</option>
                                <option value="INACTIVE">Inactive</option>
                            </Form.Control>
                        </div>
                    )}

                    {visibleKeys.has('careTypeId') && (
                        <div style={rowStyle}>
                            <span style={labelStyle}>Care Type</span>
                            <Form.Control as="select" size="sm" value={careTypeId} onChange={e => setCareTypeId(e.target.value)} style={controlStyle}>
                                <option value={KEEP}>Keep as is</option>
                                {customerCareTypes.map(ct => (
                                    <option key={ct.CareTypeID} value={ct.CareTypeID}>{ct.Name}</option>
                                ))}
                            </Form.Control>
                        </div>
                    )}

                    {visibleKeys.has('discount') && (
                        <div style={rowStyle}>
                            <span style={labelStyle}>Default Discount %</span>
                            <div style={{ ...controlStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Form.Check
                                    type="checkbox"
                                    checked={discountEnabled}
                                    onChange={e => setDiscountEnabled(e.target.checked)}
                                    label=""
                                    style={{ margin: 0 }}
                                />
                                <Form.Control
                                    type="number"
                                    size="sm"
                                    placeholder="Keep as is"
                                    value={discountValue}
                                    onChange={e => setDiscountValue(e.target.value)}
                                    disabled={!discountEnabled}
                                    min={0}
                                    max={100}
                                    style={{ flex: 1 }}
                                />
                            </div>
                        </div>
                    )}

                    {visibleKeys.has('modShared') && (
                        <div style={rowStyle}>
                            <span style={labelStyle}>Shared (Mod)</span>
                            <Form.Control as="select" size="sm" value={modShared} onChange={e => setModShared(e.target.value)} style={controlStyle}>
                                {booleanOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Form.Control>
                        </div>
                    )}

                    {visibleKeys.has('modImmobile') && (
                        <div style={rowStyle}>
                            <span style={labelStyle}>Immobile (Mod)</span>
                            <Form.Control as="select" size="sm" value={modImmobile} onChange={e => setModImmobile(e.target.value)} style={controlStyle}>
                                {booleanOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Form.Control>
                        </div>
                    )}

                    {visibleKeys.has('modEstate') && (
                        <div style={rowStyle}>
                            <span style={labelStyle}>Estate (Mod)</span>
                            <Form.Control as="select" size="sm" value={modEstate} onChange={e => setModEstate(e.target.value)} style={controlStyle}>
                                {booleanOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Form.Control>
                        </div>
                    )}
                </div>

                {isApplying && (
                    <div className="mt-3">
                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.85rem', color: 'var(--theme-text-secondary)' }}>
                            <span>Applying changes...</span>
                            <span>{progress.done} / {progress.total}</span>
                        </div>
                        <ProgressBar now={progressPercent} variant="success" style={{ height: '6px' }} />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isApplying}>Cancel</Button>
                <Button variant="primary" onClick={handleApply} disabled={isApplying}>
                    {isApplying ? 'Applying...' : 'Apply'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    borderBottom: '1px solid #dee2e6',
    gap: '12px',
    backgroundColor: 'var(--theme-surface)',
};

const labelStyle = {
    minWidth: '180px',
    fontSize: '0.9rem',
    color: 'var(--theme-text-secondary)',
};

const controlStyle = {
    flex: 1,
};

export default BulkEditCareRecipientsModal;
